import { useEffect, useState, type InputHTMLAttributes, type ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ChevronDown, CircleCheck, Clapperboard, Info, Monitor, Router, X } from 'lucide-react';
import { Badge, Button, Card, cn } from '@pixlide/ui';
import { describeBroadcastError } from '@/features/diffusion/broadcast-errors';
import type { components } from '@/api/generated/schema';
import { useRescheduleBroadcast, useScheduleBroadcast } from '@/api/queries/broadcasts';
import { FormAlert } from '@/features/auth/AuthLayout';
import { BroadcastCalendar, type DateMode } from '@/features/diffusion/BroadcastCalendar';
import { DiffusionStepper, SegmentedControl, Switch } from '@/features/diffusion/DiffusionControls';
import {
  FORMAT_LABELS,
  WEEK_DAYS,
  describeDays,
  describeLongDate,
  describeWindow,
  fromWeekDays,
  isAllDay,
  isFormatCompatible,
  todayIso,
  toScheduleInput,
  toWeekDays,
  weekDayBit,
} from '@/features/diffusion/broadcast-model';

type Schemas = components['schemas'];
type Project = Schemas['PixlideApplicationFeaturesVideoProjectsProjectListItem'];
type Screen = Schemas['PixlideApplicationFeaturesScreensScreenListItem'];
type BroadcastSummary = Schemas['PixlideApplicationFeaturesBroadcastsBroadcastListItem'];

/** Bornes reprises du Domain (`Broadcast.MaxNameLength`, `Broadcast.MaxTargets`, `DefaultPriority`). */
const NAME_MAX = 150;
const MAX_TARGETS = 500;
const NORMAL_PRIORITY = 100;

/** « Prioritaire » : passe devant les diffusions normales sur un même écran (valeur la plus basse d'abord). */
const HIGH_PRIORITY = 10;

/** Écran désactivé (`ScreenStatus.Disabled`). */
const SCREEN_DISABLED = 3;

/** Boîtier Pixlide branché en HDMI (`PlaybackMode.PixlidePlayer`) ; sinon, TV connectée. */
const PLAYBACK_PIXLIDE_PLAYER = 1;

const STEP_CONFIGURATION = 0;
const STEP_VALIDATION = 1;
const STEP_DIFFUSION = 2;

const broadcastSchema = z
  .object({
    videoProjectId: z.string().min(1, 'Choisissez la vidéo à diffuser.'),
    name: z.string().trim().max(NAME_MAX, `Le nom est limité à ${NAME_MAX} caractères.`),
    screenIds: z.array(z.string()).max(MAX_TARGETS, `Une diffusion est limitée à ${MAX_TARGETS} écrans.`),
    dateMode: z.enum(['date', 'period']),
    startsOn: z.string().min(1, 'Choisissez la date de diffusion.'),
    endsOn: z.string(),
    allDay: z.boolean(),
    startsAt: z.string(),
    endsAt: z.string(),
    days: z.array(z.number()),
    highPriority: z.boolean(),
    saveAsDraft: z.boolean(),
  })
  .superRefine((values, ctx) => {
    // Comparaison de chaînes ISO : l'ordre lexicographique est l'ordre chronologique.
    if (values.endsOn !== '' && values.endsOn < values.startsOn) {
      ctx.addIssue({ code: 'custom', path: ['endsOn'], message: 'La date de fin doit suivre la date de début.' });
    }

    // En mode « Une date », le jour est imposé par la date choisie : rien à cocher.
    if (values.dateMode === 'period' && values.days.length === 0) {
      ctx.addIssue({ code: 'custom', path: ['days'], message: 'Activez au moins un jour de diffusion.' });
    }

    if (!values.allDay) {
      if (values.startsAt === '' || values.endsAt === '') {
        ctx.addIssue({ code: 'custom', path: ['endsAt'], message: 'Indiquez une heure de début et de fin.' });
      } else if (values.startsAt === values.endsAt) {
        ctx.addIssue({ code: 'custom', path: ['endsAt'], message: "L'heure de fin doit différer de l'heure de début." });
      }
    }
  });

type BroadcastFormInput = z.infer<typeof broadcastSchema>;

/** Champs vérifiés avant de passer de « Configuration » à « Validation ». */
const CONFIGURATION_FIELDS = ['videoProjectId', 'name', 'startsOn', 'endsOn', 'days', 'startsAt', 'endsAt'] as const;

/** Raison pour laquelle un écran ne peut pas recevoir la vidéo choisie, ou `null`. */
function unavailability(screen: Screen, format: number | undefined): string | null {
  if (!screen.isPaired) return 'À appairer';
  if (screen.status === SCREEN_DISABLED) return 'Désactivé';

  if (format !== undefined && !isFormatCompatible(format, screen.orientation)) {
    return screen.orientation === 1 ? 'Écran portrait : format incompatible' : 'Écran paysage : format incompatible';
  }

  return null;
}

/** Jours réellement envoyés : en mode « Une date », le seul jour de la semaine de cette date. */
function effectiveDays(values: Pick<BroadcastFormInput, 'dateMode' | 'startsOn' | 'days'>): number[] {
  return values.dateMode === 'date' ? [weekDayBit(values.startsOn)] : values.days;
}

function describePeriod(values: Pick<BroadcastFormInput, 'dateMode' | 'startsOn' | 'endsOn'>): string {
  if (values.dateMode === 'date') return `Le ${describeLongDate(values.startsOn)}`;
  if (values.endsOn === '') return `À partir du ${describeLongDate(values.startsOn)}, sans date de fin`;

  return `Du ${describeLongDate(values.startsOn)} au ${describeLongDate(values.endsOn)}`;
}

function Section({ title, children, action }: { title: string; children: ReactNode; action?: ReactNode }) {
  return (
    <fieldset className="flex flex-col gap-4">
      <legend className="mb-4 flex w-full items-center justify-between gap-4 text-xl font-medium text-ink-900">
        {title}
        {action}
      </legend>
      {children}
    </fieldset>
  );
}

function FieldError({ message }: { message: string | undefined }) {
  return message ? (
    <p role="alert" className="text-xs font-medium text-danger-500">
      {message}
    </p>
  ) : null;
}

/** Champ horaire de la maquette : étiquette grisée (« De », « à ») collée à la saisie. */
function TimeInput({ label, ...props }: { label: string } & InputHTMLAttributes<HTMLInputElement>) {
  return (
    <label className="flex h-11 overflow-hidden rounded-lg ring-1 ring-ink-100 focus-within:ring-2 focus-within:ring-ink-900">
      <span className="grid place-items-center bg-ink-100 px-3.5 text-ink-700">{label}</span>
      <input type="time" className="w-28 bg-ink-50 px-3 text-center tabular-nums focus-visible:outline-none" {...props} />
    </label>
  );
}

function RecapRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid gap-1 py-3 sm:grid-cols-[12rem_1fr]">
      <dt className="text-sm text-ink-500">{label}</dt>
      <dd className="text-ink-900">{children}</dd>
    </div>
  );
}

interface BroadcastFormProps {
  readonly projects: readonly Project[];
  readonly screens: readonly Screen[];

  /** Diffusion à modifier ; absente pour une création. */
  readonly existing?: { readonly summary: BroadcastSummary; readonly screenIds: readonly string[] } | undefined;
  readonly onDone: () => void;
  readonly onCancel: () => void;
}

/**
 * Programmation d'une diffusion, en trois étapes comme sur la maquette :
 * 1. Configuration — quelle vidéo, quand, à quelles heures ;
 * 2. Validation — sur quels écrans, avec un récapitulatif avant envoi ;
 * 3. Diffusion — confirmation : les écrans reçoivent la vidéo dans les secondes qui suivent.
 *
 * Par défaut, la vidéo passe en continu, dès aujourd'hui, tous les jours : le client n'a pas
 * à penser en « créneaux » s'il n'en a pas besoin.
 *
 * Le modèle serveur porte UNE plage horaire commune aux jours choisis : la maquette, qui
 * montre une plage par jour, est donc ramenée à des jours à activer + une plage partagée.
 */
export function BroadcastForm({ projects, screens, existing, onDone, onCancel }: BroadcastFormProps) {
  const isEdit = existing !== undefined;
  const schedule = useScheduleBroadcast();
  const reschedule = useRescheduleBroadcast();
  const [step, setStep] = useState(STEP_CONFIGURATION);
  const [isTipVisible, setIsTipVisible] = useState(true);

  const broadcastable = projects.filter((project) => project.isBroadcastable);

  // On ne programme pas dans le passé — sauf pour garder la date d'une diffusion déjà commencée.
  const today = todayIso();
  const minDate = existing && existing.summary.startsOn < today ? existing.summary.startsOn : today;

  const {
    register,
    handleSubmit,
    setValue,
    trigger,
    getValues,
    setError,
    control,
    formState: { errors, isSubmitting },
  } = useForm<BroadcastFormInput>({
    resolver: zodResolver(broadcastSchema),
    defaultValues: existing
      ? {
          videoProjectId: existing.summary.videoProjectId,
          name: existing.summary.name,
          screenIds: [...existing.screenIds],
          dateMode: existing.summary.endsOn === existing.summary.startsOn ? 'date' : 'period',
          startsOn: existing.summary.startsOn,
          endsOn: existing.summary.endsOn ?? '',
          allDay: isAllDay(existing.summary.startsAt, existing.summary.endsAt),
          startsAt: existing.summary.startsAt.slice(0, 5),
          endsAt: existing.summary.endsAt.slice(0, 5),
          days: fromWeekDays(existing.summary.days),
          highPriority: existing.summary.priority < NORMAL_PRIORITY,
          saveAsDraft: false,
        }
      : {
          videoProjectId: broadcastable.length === 1 ? broadcastable[0]!.id : '',
          name: '',
          screenIds: [],
          dateMode: 'period',
          startsOn: today,
          endsOn: '',
          allDay: true,
          startsAt: '11:30',
          endsAt: '15:00',
          days: WEEK_DAYS.map((day) => day.bit),
          highPriority: false,
          saveAsDraft: false,
        },
  });

  const videoProjectId = useWatch({ control, name: 'videoProjectId' });
  const selectedScreenIds = useWatch({ control, name: 'screenIds' });
  const dateMode = useWatch({ control, name: 'dateMode' });
  const startsOn = useWatch({ control, name: 'startsOn' });
  const endsOn = useWatch({ control, name: 'endsOn' });
  const days = useWatch({ control, name: 'days' });
  const allDay = useWatch({ control, name: 'allDay' });
  const highPriority = useWatch({ control, name: 'highPriority' });
  const saveAsDraft = useWatch({ control, name: 'saveAsDraft' });

  const project = projects.find((candidate) => candidate.id === videoProjectId);

  // Changer de vidéo peut rendre des écrans déjà cochés incompatibles (paysage → portrait) :
  // ils sont décochés plutôt que de laisser partir une requête que le serveur refusera.
  useEffect(() => {
    const kept = selectedScreenIds.filter((id) => {
      const screen = screens.find((candidate) => candidate.id === id);

      return screen !== undefined && unavailability(screen, project?.format) === null;
    });

    if (kept.length !== selectedScreenIds.length) {
      setValue('screenIds', kept, { shouldValidate: false });
    }
  }, [project?.format, screens, selectedScreenIds, setValue]);

  const goToValidation = async () => {
    if (await trigger(CONFIGURATION_FIELDS)) {
      setStep(STEP_VALIDATION);
    }
  };

  const onSubmit = handleSubmit(async (values) => {
    if (values.screenIds.length === 0) {
      // Vérifié ici et non dans le schéma : l'étape « Configuration » ne demande pas d'écran.
      setError('screenIds', { message: 'Sélectionnez au moins un écran.' });
      return;
    }

    const scheduleInput = toScheduleInput({
      ...values,
      endsOn: values.dateMode === 'date' ? values.startsOn : values.endsOn,
      days: effectiveDays(values),
    });

    try {
      if (existing) {
        await reschedule.mutateAsync({
          broadcastId: existing.summary.id,
          input: { schedule: scheduleInput, screenIds: values.screenIds },
        });
      } else {
        await schedule.mutateAsync({
          videoProjectId: values.videoProjectId,
          // Sans nom saisi, la diffusion prend celui de la vidéo : c'est ce que le client
          // reconnaîtra dans la liste.
          name: values.name === '' ? (project?.name ?? 'Diffusion') : values.name,
          screenIds: values.screenIds,
          schedule: scheduleInput,
          priority: values.highPriority ? HIGH_PRIORITY : NORMAL_PRIORITY,
          publishImmediately: !values.saveAsDraft,
        });
      }

      setStep(STEP_DIFFUSION);
    } catch {
      // Affiché par FormAlert via l'état d'erreur de la mutation.
    }
  });

  const failure = schedule.error ?? reschedule.error;

  if (!isEdit && broadcastable.length === 0) {
    return (
      <Card className="flex flex-col items-start gap-3 p-6">
        <h3 className="text-lg font-semibold">Aucune vidéo prête à diffuser</h3>
        <p className="text-sm text-ink-500">
          Une vidéo devient diffusable une fois son rendu terminé. Créez-en une, ou attendez la fin d'un rendu en cours.
        </p>
        <div className="flex gap-2">
          <Link to="/creer" className="font-medium text-brand-700 hover:underline">
            Créer une vidéo
          </Link>
          <Button variant="ghost" size="sm" onClick={onCancel}>
            Fermer
          </Button>
        </div>
      </Card>
    );
  }

  const visibleDays = dateMode === 'date' ? WEEK_DAYS.filter((day) => day.bit === weekDayBit(startsOn)) : WEEK_DAYS;

  return (
    <div className="flex flex-col gap-6">
      <DiffusionStepper current={step} />

      {isTipVisible && step === STEP_CONFIGURATION ? (
        <div className="mx-auto flex w-full max-w-2xl items-start gap-3 rounded-xl bg-white/60 px-4 py-3 text-sm text-ink-700 ring-1 ring-white">
          <Info aria-hidden className="mt-0.5 size-5 shrink-0 text-ink-500" />
          <p className="flex-1">
            Choisissez la vidéo, puis quand elle doit passer. Vous choisirez les écrans à l'étape suivante ; rien n'est
            envoyé avant votre validation.
          </p>
          <button type="button" aria-label="Masquer le conseil" onClick={() => setIsTipVisible(false)} className="text-ink-500 hover:text-ink-900">
            <X aria-hidden className="size-4" />
          </button>
        </div>
      ) : null}

      <Card className="mx-auto w-full max-w-3xl p-5 sm:p-10">
        <form onSubmit={onSubmit} noValidate className="flex flex-col gap-10">
          {failure && step === STEP_VALIDATION ? <FormAlert message={describeBroadcastError(failure)} /> : null}

          {step === STEP_CONFIGURATION ? (
            <>
              <div className="flex flex-col gap-2">
                <div className="relative inline-flex w-full max-w-sm items-center rounded-xl bg-ink-50/70 ring-1 ring-ink-100 focus-within:ring-2 focus-within:ring-ink-900">
                  <Clapperboard aria-hidden className="pointer-events-none absolute left-4 size-6 text-ink-900" strokeWidth={1.5} />
                  {/* Un <select> natif : clavier, recherche par frappe et sélecteur mobile gratuits. */}
                  <select
                    aria-label="Vidéo à diffuser"
                    disabled={isEdit}
                    className="h-14 w-full cursor-pointer appearance-none bg-transparent pr-12 pl-14 font-medium text-ink-900 focus-visible:outline-none disabled:cursor-default"
                    {...register('videoProjectId')}
                  >
                    <option value="" disabled>
                      Sélectionnez un projet vidéo
                    </option>
                    {(isEdit ? projects.filter((candidate) => candidate.id === videoProjectId) : broadcastable).map((candidate) => (
                      <option key={candidate.id} value={candidate.id}>
                        {candidate.name} · {FORMAT_LABELS[candidate.format]}
                      </option>
                    ))}
                  </select>
                  {isEdit ? null : (
                    <span aria-hidden className="pointer-events-none absolute right-3 grid size-7 place-items-center rounded-full bg-white shadow-card">
                      <ChevronDown className="size-4" />
                    </span>
                  )}
                </div>
                {project && project.broadcastScreenCount > 0 ? (
                  <p className="text-sm text-ink-500">Déjà diffusée sur {project.broadcastScreenCount} écran(s).</p>
                ) : null}
                <FieldError message={errors.videoProjectId?.message} />
              </div>

              <div className="flex flex-col gap-1.5">
                <div className="flex items-center border-y border-ink-100 py-4">
                  <button
                    type="button"
                    aria-label="Effacer le nom"
                    disabled={isEdit}
                    onClick={() => setValue('name', '')}
                    className="grid size-10 shrink-0 place-items-center text-ink-500 hover:text-ink-900 disabled:invisible"
                  >
                    <X aria-hidden className="size-5" strokeWidth={1.5} />
                  </button>
                  <span aria-hidden className="mx-3 h-10 w-px bg-ink-100" />
                  <input
                    aria-label="Nom de la diffusion"
                    placeholder={project ? `Nommer votre diffusion (par défaut : ${project.name})` : 'Nommer votre projet ou de la diffusion'}
                    readOnly={isEdit}
                    maxLength={NAME_MAX}
                    className="min-w-0 flex-1 bg-transparent py-2 text-lg text-ink-900 placeholder:text-ink-500 focus-visible:outline-none sm:text-xl"
                    {...register('name')}
                  />
                </div>
                <FieldError message={errors.name?.message} />
              </div>

              <SegmentedControl<DateMode>
                label="Type de diffusion"
                value={dateMode}
                options={[
                  { value: 'date', label: 'Une date' },
                  { value: 'period', label: 'Une période' },
                ]}
                onChange={(mode) => {
                  setValue('dateMode', mode);
                  // Une date unique se ramène à une période d'un jour ; l'inverse repart sans fin.
                  setValue('endsOn', mode === 'date' ? getValues('startsOn') : '');
                }}
              />

              <Section title="Choisissez la date">
                <BroadcastCalendar
                  // Le calendrier garde sa page affichée en état local : on le remonte au
                  // changement de mode pour qu'il se recale sur la date choisie.
                  key={dateMode}
                  mode={dateMode}
                  startsOn={startsOn}
                  endsOn={endsOn}
                  minDate={minDate}
                  onChange={(range) => {
                    setValue('startsOn', range.startsOn, { shouldValidate: true });
                    setValue('endsOn', range.endsOn, { shouldValidate: true });
                  }}
                />
                <p aria-live="polite" className="text-sm text-ink-500">
                  {describePeriod({ dateMode, startsOn, endsOn })}
                  {dateMode === 'period' && endsOn === '' ? ' — cliquez sur un second jour pour fixer la fin.' : '.'}
                </p>
                <FieldError message={errors.startsOn?.message ?? errors.endsOn?.message} />
              </Section>

              <Section title="Choisissez un horaire de diffusion">
                <div className="flex flex-col gap-4 rounded-xl p-5 ring-1 ring-ink-100">
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <p className="text-lg text-ink-900">Toute la journée</p>
                      <p className="text-sm text-ink-500">Plage horaire appliquée à chaque jour de diffusion.</p>
                    </div>
                    <Switch label="Toute la journée" checked={allDay} onChange={(checked) => setValue('allDay', checked)} />
                  </div>
                  {allDay ? null : (
                    <div className="flex flex-col gap-2">
                      <div className="flex flex-wrap items-center gap-3">
                        <TimeInput label="De" aria-label="Heure de début" {...register('startsAt')} />
                        <TimeInput label="à" aria-label="Heure de fin" {...register('endsAt')} />
                      </div>
                      <p className="text-xs text-ink-500">
                        Une heure de fin avant l'heure de début prolonge jusqu'au lendemain (service du soir).
                      </p>
                      <FieldError message={errors.endsAt?.message} />
                    </div>
                  )}
                </div>

                <ul className="flex flex-col gap-2" aria-label="Jours de diffusion">
                  {visibleDays.map((day) => {
                    // En mode « Une date », le jour découle de la date : affiché, mais figé.
                    const isLocked = dateMode === 'date';
                    const isActive = isLocked || days.includes(day.bit);

                    return (
                      <li
                        key={day.bit}
                        className={cn(
                          'flex items-center justify-between rounded-xl px-6 py-3 transition',
                          isActive ? 'bg-white ring-1 ring-ink-100' : 'bg-ink-50',
                        )}
                      >
                        <span className="text-lg text-ink-900">{day.label}</span>
                        <Switch
                          label={`Diffuser le ${day.label.toLowerCase()}`}
                          checked={isActive}
                          disabled={isLocked}
                          onChange={(checked) =>
                            setValue(
                              'days',
                              checked ? [...days, day.bit].sort((a, b) => a - b) : days.filter((bit) => bit !== day.bit),
                              { shouldValidate: true },
                            )
                          }
                        />
                      </li>
                    );
                  })}
                </ul>
                <FieldError message={errors.days?.message} />
              </Section>

              {isEdit ? null : (
                <div className="flex items-center justify-between gap-4 rounded-xl px-6 py-4 ring-1 ring-ink-100">
                  <div>
                    <p className="text-lg text-ink-900">Diffusion prioritaire</p>
                    <p className="text-sm text-ink-500">Passe avant les autres diffusions sur ces écrans (promotion, annonce).</p>
                  </div>
                  <Switch label="Diffusion prioritaire" checked={highPriority} onChange={(checked) => setValue('highPriority', checked)} />
                </div>
              )}

              <div className="flex flex-col gap-4 border-t border-ink-100 pt-6 sm:flex-row sm:items-center">
                {isEdit ? null : (
                  <label className="flex cursor-pointer items-center gap-3 text-lg text-ink-900">
                    <input type="checkbox" className="peer sr-only" {...register('saveAsDraft')} />
                    <span
                      aria-hidden
                      className="grid size-5 place-items-center rounded-full ring-1 ring-ink-300 peer-checked:ring-success-500 peer-focus-visible:outline-2 peer-focus-visible:outline-brand-500"
                    >
                      <span className={cn('size-2.5 rounded-full', saveAsDraft ? 'bg-success-500' : 'bg-transparent')} />
                    </span>
                    Enregistrer comme brouillon
                  </label>
                )}
                <div className="flex gap-3 sm:ml-auto">
                  <Button type="button" variant="secondary" onClick={onCancel}>
                    Annuler
                  </Button>
                  <Button type="button" onClick={() => void goToValidation()}>
                    Valider
                  </Button>
                </div>
              </div>
            </>
          ) : null}

          {step === STEP_VALIDATION ? (
            <>
              <Section title="Sur quels écrans ?">
                {screens.length === 0 ? (
                  <p className="text-sm text-ink-500">
                    Aucun écran déclaré.{' '}
                    <Link to="/ecrans" className="font-medium text-brand-700 hover:underline">
                      Déclarer et appairer un écran
                    </Link>
                  </p>
                ) : (
                  <ul className="grid gap-3 sm:grid-cols-2">
                    {screens.map((screen) => {
                      const reason = unavailability(screen, project?.format);
                      const isChecked = selectedScreenIds.includes(screen.id);
                      const DeviceIcon = screen.playbackMode === PLAYBACK_PIXLIDE_PLAYER ? Router : Monitor;

                      return (
                        <li key={screen.id}>
                          <label
                            className={cn(
                              'flex h-full items-center gap-4 rounded-xl p-4 ring-1 transition',
                              reason !== null
                                ? 'cursor-not-allowed bg-ink-50 opacity-60 ring-ink-100'
                                : isChecked
                                  ? 'cursor-pointer bg-white ring-2 ring-ink-900'
                                  : 'cursor-pointer bg-white ring-ink-100 hover:ring-ink-300',
                            )}
                          >
                            <input
                              type="checkbox"
                              value={screen.id}
                              disabled={reason !== null}
                              className="size-4 accent-ink-900"
                              {...register('screenIds')}
                            />
                            <DeviceIcon aria-hidden className="size-8 shrink-0 text-ink-900" strokeWidth={1.25} />
                            <span className="min-w-0 flex-1">
                              <span className="block truncate font-medium text-ink-900">{screen.name}</span>
                              <span className="block truncate text-sm text-ink-500">
                                {screen.establishmentName} · {screen.orientation === 1 ? 'Portrait' : 'Paysage'} ·{' '}
                                {screen.playbackMode === PLAYBACK_PIXLIDE_PLAYER ? 'Boîtier Pixlide' : 'TV connectée'}
                              </span>
                              {reason !== null ? (
                                <span className="mt-1.5 block">
                                  <Badge tone="warning">{reason}</Badge>
                                </span>
                              ) : null}
                            </span>
                          </label>
                        </li>
                      );
                    })}
                  </ul>
                )}
                <FieldError message={errors.screenIds?.message} />
              </Section>

              <Section title="Récapitulatif">
                <dl className="divide-y divide-ink-100 border-y border-ink-100">
                  <RecapRow label="Vidéo">
                    {project?.name ?? existing?.summary.videoProjectName}
                    {project ? <span className="text-ink-500"> · {FORMAT_LABELS[project.format]}</span> : null}
                  </RecapRow>
                  <RecapRow label="Nom de la diffusion">{getValues('name') || project?.name}</RecapRow>
                  <RecapRow label="Date">{describePeriod({ dateMode, startsOn, endsOn })}</RecapRow>
                  <RecapRow label="Jours">{describeDays(toWeekDays(effectiveDays({ dateMode, startsOn, days })))}</RecapRow>
                  <RecapRow label="Horaire">
                    {allDay ? 'Toute la journée' : describeWindow(getValues('startsAt'), getValues('endsAt'))}
                  </RecapRow>
                  {isEdit ? null : (
                    <RecapRow label="Envoi">
                      {saveAsDraft ? 'Brouillon : rien ne part sur les écrans avant publication' : 'Publication immédiate'}
                      {highPriority ? ' · prioritaire' : ''}
                    </RecapRow>
                  )}
                </dl>
              </Section>

              <div className="flex justify-end gap-3 border-t border-ink-100 pt-6">
                <Button type="button" variant="secondary" onClick={() => setStep(STEP_CONFIGURATION)}>
                  Retour
                </Button>
                <Button type="submit" isLoading={isSubmitting}>
                  {isEdit ? 'Enregistrer' : saveAsDraft ? 'Enregistrer le brouillon' : 'Diffuser'}
                </Button>
              </div>
            </>
          ) : null}

          {step === STEP_DIFFUSION ? (
            <div role="status" className="flex flex-col items-center gap-4 py-6 text-center">
              <CircleCheck aria-hidden className="size-14 text-success-500" strokeWidth={1.25} />
              <h3 className="text-2xl font-medium text-ink-900">
                {isEdit ? 'Diffusion mise à jour' : saveAsDraft ? 'Brouillon enregistré' : 'Votre diffusion est programmée'}
              </h3>
              <p className="max-w-md text-ink-500">
                {saveAsDraft && !isEdit
                  ? 'Publiez-la depuis la liste de vos diffusions quand vous serez prêt.'
                  : 'Les écrans concernés reçoivent la nouvelle programmation dans les secondes qui suivent.'}
              </p>
              <Button type="button" onClick={onDone}>
                Voir mes diffusions
              </Button>
            </div>
          ) : null}
        </form>
      </Card>
    </div>
  );
}
